import { createSignal } from "solid-js";
import { accountIdForAlias } from "./account-alias";

export const TAB_ACCOUNT_STORAGE_KEY = "tab_account";
export const LAST_ACTIVE_ACCOUNT_KEY = "last_active_account";
export const TAB_ACCOUNT_CHANGED_EVENT = "gymlog:tab-account-changed";
export const ACCOUNT_HANDOFF_PARAM = "as";

interface TabAccountChangeEventDetail {
  userId?: string | null;
}

export interface WriteTabAccountOptions {
  silent?: boolean;
}

let handoffConsumed = false;

const [activeTabAccountSignal, setActiveTabAccountSignal] = createSignal<string | null>(
  readTabAccountIdInitial(),
);

if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, () => {
    setActiveTabAccountSignal(readTabAccountId());
  });
}

function readTabAccountIdInitial(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    if (!handoffConsumed) {
      handoffConsumed = true;
      const searchParams = new URLSearchParams(window.location.search);
      const alias = searchParams.get("as");
      if (alias) {
        searchParams.delete("as");
        const cleanSearch = searchParams.toString();
        const newUrl =
          window.location.pathname + (cleanSearch ? `?${cleanSearch}` : "") + window.location.hash;
        window.history.replaceState(null, "", newUrl);

        const targetUserId = accountIdForAlias(alias);
        if (targetUserId) {
          try {
            window.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, targetUserId);
          } catch {
            // Storage unavailable
          }
          return targetUserId;
        }
      }
    }

    const currentTab = window.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
    if (currentTab) {
      return currentTab;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Returns the currently active tab account ID signal (reactive Solid accessor).
 */
export function activeTabAccountId(): string | null {
  return activeTabAccountSignal();
}

/**
 * Reads the active tab account ID from sessionStorage synchronously.
 */
export function readTabAccountId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Reads the last active account ID from localStorage synchronously.
 */
export function readLastActiveAccountId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/**
 * Dispatches the custom event signaling a tab account change.
 */
export function notifyTabAccountChanged(userId?: string | null): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.dispatchEvent(
      new CustomEvent<TabAccountChangeEventDetail>(TAB_ACCOUNT_CHANGED_EVENT, {
        detail: { userId },
      }),
    );
  } catch {
    // Ignore error
  }
}

/**
 * Writes the active account ID to per-tab sessionStorage and mirrors to localStorage as last active.
 * Updates the reactive signal and dispatches the tab-account-changed event unless silent is true.
 */
export function writeTabAccountId(userId: string, options?: WriteTabAccountOptions): void {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
      window.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, userId);
    }
  } catch {
    // sessionStorage or localStorage not available
  }
  setActiveTabAccountSignal(userId);
  if (!options?.silent) {
    notifyTabAccountChanged(userId);
  }
}

/**
 * Clears the active tab account ID from sessionStorage.
 * Updates the reactive signal and dispatches the tab-account-changed event.
 */
export function clearTabAccountId(): void {
  try {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(TAB_ACCOUNT_STORAGE_KEY);
    }
  } catch {
    // sessionStorage not available
  }
  setActiveTabAccountSignal(null);
  notifyTabAccountChanged(null);
}
